import { CLOSE_LOADER, OPEN_LOADER } from "../store/Loader/loader.type";
import { baseURL, secretKey } from "./config";
import store from "../store/Provider";

export const openSpinner = () => {
  return store.dispatch({ type: OPEN_LOADER });
};

export const closeSpinner = () => {
  return store.dispatch({ type: CLOSE_LOADER });
};

// Helper function to get latest headers
const getUpdatedHeaders = () => ({
  "Content-Type": "application/json",
  key: secretKey, // Will always use the latest secretKey
  Authorization: `Bearer ${localStorage.getItem("token")}`, // Gets fresh token
});

const getBaseURL = () => {
  const url = baseURL || "http://localhost:5000/";
  return url.endsWith("/") ? url : `${url}/`;
};

export const apiInstanceFetch = {
  get baseURL() {
    return getBaseURL();
  },
  headers: {
    "Content-Type": "application/json",
    key: `${secretKey}`,
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
  get: (url, showSpinner = true) => {
    if (showSpinner) openSpinner();
    return fetch(`${getBaseURL()}${url}`, {
      method: "GET",
      headers: getUpdatedHeaders(),
      // Avoid 304 responses with empty bodies breaking JSON parsing
      cache: "no-store",
    })
      .then(handleErrors)
      .finally(() => {
        if (url && typeof url === 'string' && url.includes('city?regionId='))
          setTimeout(() => {
            closeSpinner()
          }, 800);
        else
          closeSpinner()
      });
  },
  getFile: (url, showSpinner = true) => {
    if (showSpinner) openSpinner();
    return fetch(`${getBaseURL()}${url}`, {
      method: "GET",
      headers: getUpdatedHeaders(),
      cache: "no-store",
    })
      .then(handleFileResponse)
      .finally(() => closeSpinner());
  },

  post: (url, data) => {
    openSpinner();
    return fetch(`${getBaseURL()}${url}`, {
      method: "POST",
      headers: getUpdatedHeaders(),
      body: JSON.stringify(data),
    })
      .then(handleErrors)
      .finally(() => closeSpinner());
  },

  postFormData: (url, formData, showSpinner = true) => {
    if (showSpinner) openSpinner();
    const headers = getUpdatedHeaders();
    delete headers["Content-Type"];
    return fetch(`${getBaseURL()}${url}`, {
      method: "POST",
      headers,
      body: formData,
    })
      .then(handleErrors)
      .finally(() => {
        if (showSpinner) closeSpinner();
      });
  },

  patch: (url, data) => {
    openSpinner();
    return fetch(`${getBaseURL()}${url}`, {
      method: "PATCH",
      headers: getUpdatedHeaders(),
      body: JSON.stringify(data),
    })
      .then(handleErrors)
      .finally(() => closeSpinner());
  },

  put: (url, data) => {
    openSpinner();
    return fetch(`${getBaseURL()}${url}`, {
      method: "PUT",
      headers: getUpdatedHeaders(),
      body: JSON.stringify(data),
    })
      .then(handleErrors)
      .finally(() => closeSpinner());
  },

  delete: (url) => {
    openSpinner();
    return fetch(`${getBaseURL()}${url}`, {
      method: "DELETE",
      headers: getUpdatedHeaders(),
    })
      .then(handleErrors)
      .finally(() => closeSpinner());
  },
};

async function handleErrors(response) {
  if (!response.ok) {
    // Surface the server's JSON error message (e.g. validation / 409 conflicts)
    // instead of a generic "HTTP error! Status: undefined".
    let message = `Request failed (status ${response.status}).`;
    try {
      const text = await response.text();
      if (text) {
        const data = JSON.parse(text);
        if (data && data.message) message = data.message;
      }
    } catch {
      /* non-JSON body — keep the generic status message */
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  // 304/204 typically has no body; treat as empty JSON.
  if (response.status === 304 || response.status === 204) return {};

  // Some APIs may respond with empty body (or non-JSON) even on 200.
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

async function handleFileResponse(response) {
  if (!response.ok) {
    const text = await response.text();
    let message = `HTTP error! Status: ${response.status}`;
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (parsed?.message) message = parsed.message;
      } catch {
        message = text.slice(0, 200) || message;
      }
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  return {
    blob,
    contentDisposition: response.headers.get("content-disposition") || "",
    contentType: response.headers.get("content-type") || "",
    payoutExcelSummaryId: response.headers.get("x-payout-excel-summary-id") || "",
    payoutPeriod: response.headers.get("x-payout-period") || "",
    payoutRecordsCount: response.headers.get("x-payout-records-count") || "",
    coveragePercent: response.headers.get("x-payout-coverage-percent") || "",
    isPartialCoverage: response.headers.get("x-payout-is-partial-coverage") === "true",
    effectivePeriodStart: response.headers.get("x-payout-effective-period-start") || "",
    effectivePeriodEnd: response.headers.get("x-payout-effective-period-end") || "",
    coverageMessage: (() => {
      const raw = response.headers.get("x-payout-coverage-message") || "";
      if (!raw) return "";
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    })(),
    finalTransferBatchId: response.headers.get("x-final-transfer-batch-id") || "",
    finalTransferRecordsCount: response.headers.get("x-final-transfer-records-count") || "",
  };
}
